import { Link } from "wouter";
import { SEO } from "@/components/SEO";
import { PageHero } from "@/components/ui/page-hero";
import { heroImages, maintenanceShowcaseImages, showcaseImages } from "@/lib/image-map";
import { serviceAreaLinks } from "@/lib/site-navigation";
import { serviceSchema } from "@/lib/organization";
import { PictureImage } from "@/components/ui/picture-image";

const description =
  "Lawn care services in Norfolk County from TLC Associates LLC, including mowing, edging, turf care, seasonal cleanup, and weed control.";

export default function LawnCare() {
  return (
    <>
      <SEO
        title="Lawn Care Services | TLC Associates LLC"
        description={description}
        path="/lawn-care"
        schema={serviceSchema("/lawn-care", "Lawn Care", "Lawn care service")}
      />
      <PageHero
        imageSrc={heroImages["/lawn-care"]}
        eyebrow="Lawn Care Service in Norfolk County, Massachusetts"
        headline="Healthy Lawns Through Every Season"
        description="Weekly mowing, turf care, and seasonal cleanups that keep lawns across Norfolk County healthy and sharp."
      />
      <section className="bg-[#f7f3ed] py-20">
        <div className="container mx-auto max-w-4xl px-4">
          <p className="mb-10 text-xl leading-relaxed text-gray-700">
             A well-maintained property should not require your weekend. Homeowners and
             property managers across Norfolk County rely on TLC Associates LLC for
             consistent, detailed lawn maintenance and turf that stays sharp.
          </p>
          <h2 className="mb-8 text-3xl text-[#1a3a1a]">Lawn Care Services</h2>
          <div className="space-y-8 text-lg leading-relaxed text-gray-700">
            <div>
              <h3 className="mb-2 text-2xl text-[#1a3a1a]">Mowing, Trimming &amp; Edging</h3>
              <p>
                 Your lawn is the foundation of your property's curb appeal. TLC Associates LLC
                 provides precision mowing, string trimming, and power edging to create clean,
                 sharp lines.
              </p>
            </div>
            <div>
              <h3 className="mb-2 text-2xl text-[#1a3a1a]">Turf Care</h3>
              <p>
                 We handle fertilization, aeration, and overseeding to keep the turf dense
                 and resilient throughout the New England seasons. Weed control and thatch
                 removal ensure your grass has what it needs to thrive.
              </p>
            </div>
            <div>
              <h3 className="mb-2 text-2xl text-[#1a3a1a]">Seasonal Cleanups</h3>
              <p>
                 Spring and fall in Massachusetts demand serious work. Our seasonal cleanups
                 prepare your property for the growing season or put it to bed for winter.
                 We handle extensive leaf removal, cut back perennials, clear out downed
                 branches, and ensure your grounds are pristine.
              </p>
            </div>
          </div>
          <h2 className="mb-8 mt-16 text-3xl text-[#1a3a1a]">
            Lawn Care Service Areas in Norfolk County
          </h2>
          <ul className="grid gap-4 sm:grid-cols-2">
            {serviceAreaLinks.map((area) => (
              <li key={area.path}>
                <h3 className="text-xl">
                  <Link className="text-[#2a7a2a] hover:underline" href={area.path}>
                    {area.name}
                  </Link>
                </h3>
              </li>
            ))}
          </ul>
        </div>
      </section>
      <LawnCareMedia />
    </>
  );
}

function LawnCareMedia() {
  const lawnImages = [
    {
      src: maintenanceShowcaseImages.lawnStriping.desktop,
      smallSrc: maintenanceShowcaseImages.lawnStriping.mobile,
      alt: "Precision-striped hillside lawn with retaining wall",
    },
    {
      src: (showcaseImages[23] as any).src,
      alt: "Precision lawn mowing concentric circles pattern",
    },
    {
      src: (showcaseImages[24] as any).src,
      alt: "Lawn care with flower bed maintenance",
    },
    {
      src: (showcaseImages[26] as any).src,
      alt: "Residential lawn mowing striping",
    },
  ];
  const maintenanceImages = [
    {
      src: (showcaseImages[19] as any).src,
      alt: "Residential landscape maintenance with fresh mulch beds",
    },
    {
      src: maintenanceShowcaseImages.lakefront.desktop,
      smallSrc: maintenanceShowcaseImages.lakefront.mobile,
      alt: "Lakefront property with manicured shrubs and hostas",
    },
    {
      src: maintenanceShowcaseImages.scagMower.desktop,
      smallSrc: maintenanceShowcaseImages.scagMower.mobile,
      alt: "SCAG commercial zero-turn mower",
    },
  ];
  return (
    <>
      <section className="bg-[#1a3a1a] py-24">
        <div className="container mx-auto max-w-6xl px-4">
          <h2 className="mb-16 text-center text-3xl text-white md:text-4xl">See the Difference</h2>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {lawnImages.map(({ src, smallSrc, alt }) => (
              <div key={src} className="aspect-[4/3] overflow-hidden rounded-xl shadow-md">
                <PictureImage
                  src={src}
                  smallSrc={smallSrc}
                  mediaQuery="all"
                  sizes="(max-width: 639px) 100vw, (max-width: 1023px) 50vw, 25vw"
                  alt={alt}
                  width={800}
                  height={600}
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="bg-white py-20">
        <div className="container mx-auto max-w-6xl px-4">
          <h2 className="mb-12 text-center text-3xl text-[#1a3a1a] md:text-4xl">Our Maintenance Work</h2>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {maintenanceImages.map(({ src, smallSrc, alt }) => (
              <div key={src} className="aspect-square overflow-hidden rounded-xl shadow-md">
                <PictureImage
                  src={src}
                  smallSrc={smallSrc}
                  mediaQuery="all"
                  sizes="(max-width: 767px) 100vw, min(368px, 33.333vw)"
                  alt={alt}
                  width={800}
                  height={600}
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}